import { Component, forwardRef, signal } from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Select } from "primeng/select";
import { InputText } from "primeng/inputtext";
import { DIAL_CODES } from "../../constants/dial-codes";
import { joinPhone, splitPhone } from "../../common/phone";

/**
 * Phone field with the calling code in a select, so nobody has to type +39 or 0039.
 * The form value stays one string ("+39 3331234567"); a number pasted with +xx or 00xx moves its prefix to the select.
 */
@Component({
  standalone: true,
  selector: "app-phone-input",
  imports: [FormsModule, Select, InputText],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneInputComponent), multi: true }],
  template: `
    <div class="phone-input">
      <p-select class="phone-dial" [options]="options" [ngModel]="dial()" (ngModelChange)="setDial($event)"
        optionLabel="label" optionValue="dial" [filter]="true" filterBy="label" [disabled]="disabled()"
        [ngModelOptions]="{ standalone: true }"></p-select>
      <input class="phone-number" type="tel" pInputText autocomplete="tel-national" inputmode="tel" maxlength="20"
        [ngModel]="number()" (ngModelChange)="setNumber($event)" (blur)="touched()" [disabled]="disabled()"
        [ngModelOptions]="{ standalone: true }" />
    </div>
  `,
  styles: [`
    .phone-input { display: flex; gap: .5rem; width: 100%; }
    .phone-dial { flex: 0 0 8.5rem; }
    .phone-number { flex: 1; min-width: 0; }
  `],
})
export class PhoneInputComponent implements ControlValueAccessor {
  readonly options = DIAL_CODES.map((c) => ({ dial: c.dial, label: `${c.iso} ${c.dial}` }));
  readonly dial = signal("+39");
  readonly number = signal("");
  readonly disabled = signal(false);

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    const parts = splitPhone(value);
    this.dial.set(parts.dial);
    this.number.set(parts.number);
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled.set(isDisabled); }
  touched(): void { this.onTouched(); }

  setDial(dial: string): void {
    this.dial.set(dial);
    this.emit();
  }

  setNumber(raw: string): void {
    const typed = String(raw ?? "").trim();
    if (typed.startsWith("+") || typed.startsWith("00")) {
      const parts = splitPhone(typed, this.dial());
      this.dial.set(parts.dial);
      this.number.set(parts.number);
    } else {
      this.number.set(typed.replace(/[^\d]/g, ""));
    }
    this.emit();
  }

  private emit(): void {
    this.onChange(joinPhone(this.dial(), this.number()));
  }
}
